import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import DialogForm from '../Global/Modals/DialogForm';
import OutlinedSelectInput from '../Global/Inputs/OutlinedSelect';
import CheckboxInput from '../Global/Inputs/Checkbox';
import TextFieldInput from '../Global/Inputs/TextField';
import PhoneField from '../Global/Inputs/PhoneField';
import ProgressButton from '../Global/Buttons/Progress';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { Formik, FastField } from 'formik';
import _get from 'lodash/get';

import Grid from '@material-ui/core/Grid';
import GridItem from './../Global/GridItem';
import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import TypographyStyles from '../../styles/typography';
import ButtonStyles from '../../styles/button';
import SpacingStyles from '../../styles/helpers/spacing';

import { DATATYPES } from '../../constants/constants';
import { UNIT_ABBREVIATIONS, CURRENCY_SYMBOL } from '../../constants/constants';

import ProductFormSchema from './ProductForm.schema';
import getProductModel from '../../Models/Product';

import { AppContext } from '../../AppProvider';

const styles = theme => ({
  titleContainer: {
    position: 'relative'
  },
  title: {
    fontWeight: 'bold !important'
  },
  fieldsMarginBottom: {
    marginBottom: `${theme.spacing.unit * 2.5}px !important`
  },
  hasMatCheckboxContainer: {
    alignItems: 'center',
    display: 'flex'
  },
  hasMatCheckboxLabel: {
    fontWeight: '400 !important'
  },
  toggleSwitch: {
    alignItems: 'center',
    display: 'flex',
    position: 'absolute',
    top: -10,
    right: 0
  },
  toggleSwitchLeftLabel: {
    marginRight: `${theme.spacing.unit * 1.5}px !important`
  },
  toggleSwitchLabel: {
    color: `${theme.typography.color.second} !important`
  },
  inactiveLable: {
    cursor: 'pointer'
  }
});

export class ProductForm extends Component {
  static propTypes = {
    product: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
  };

  state = {
    product: {},
    hazMatDisplayed: false
  };

  componentDidMount() {
    this.setState({
      product: getProductModel()
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.product !== prevProps.product) {
      this.setState({
        product: { ...this.props.product }
      });
    }
    if (this.state.hazMatDisplayed) {
      this._scrollBottom();
    }
  }

  _scrollBottom = () => {
    if (this.modal && this.state.hazMatDisplayed) {
      this.modal.scrollTop = this.modal.scrollHeight;
    }
  };

  _displayHazMatFields = () => {
    this.setState({ hazMatDisplayed: !this.state.hazMatDisplayed });
  };

  _handleOnClose = () => {
    const { onClose } = this.props;
    this.setState(
      {
        product: {
          ...this.props.product
        },
        hazMatDisplayed: false
      },
      onClose
    );
  };

  _handleOnSave = async product => {
    const { onSave } = this.props;
    try {
      await onSave(product);
      return true;
    } catch {
      return false;
    }
  };

  _handleOnRemove = () => {
    const { onRemove } = this.props;
    onRemove(this.state.id);
  };

  _formDidNotChange = formValues => isEqual(formValues, this.props.product);

  _inactiveSwitchAction = (value, setFieldValue) =>
    setFieldValue('active', !value);

  render() {
    const { classes, open, t, referenceFields, isSubmitting } = this.props;
    const { product } = this.state;

    const productId = product.id;

    const {
      orgInfo: {
        config: { defaultCountry }
      }
    } = this.context.globalProps;

    return (
      <Formik
        validationSchema={ProductFormSchema({ t })}
        enableReinitialize={true}
        handleSubmit={this._handleOnSave}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          ...product
        }}
        onSubmit={async (values, actions) => {
          const saved = await this._handleOnSave(values);
          if (saved) {
            actions.resetForm();
          }
        }}
        onReset={(values, actions) => {
          actions.resetForm();
          this._handleOnClose();
        }}
        render={({
          values,
          handleChange,
          handleSubmit,
          handleReset,
          setFieldValue,
          dirty
        }) => (
          <DialogForm
            open={open}
            onClose={handleReset}
            refObject={{
              modal: element => (this.modal = element)
            }}
            title={
              <div className={classes.titleContainer}>
                <p className={classnames(classes.headline6, classes.title)}>
                  {productId ? t('Edit Product') : t('Add Product')}
                </p>
                {values.id && (
                  <div className={classes.toggleSwitch}>
                    <p
                      className={classnames(
                        classes.body2,
                        classes.inactiveLable,
                        {
                          [classes.toggleSwitchLabel]: !values.active
                        }
                      )}
                      onClick={() =>
                        this._inactiveSwitchAction(values.active, setFieldValue)
                      }
                    >
                      {t('Inactive')}
                    </p>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.active || false}
                          onChange={({ target }) => {
                            handleChange({
                              target: {
                                name: target.value,
                                value: target.checked
                              }
                            });
                          }}
                          value="active"
                          color="primary"
                        />
                      }
                      label={t('Active')}
                      classes={{
                        root: classes.noMargin,
                        label: classnames(classes.body2, {
                          [classes.toggleSwitchLabel]: values.active
                        })
                      }}
                    />
                  </div>
                )}
              </div>
            }
            footer={
              <Fragment>
                <div className={classes.spacer} />
                <Button
                  className={classes.button}
                  onClick={handleReset}
                  color={'primary'}
                  classes={{ label: classes.buttonLabel }}
                >
                  {t('Cancel')}
                </Button>
                <ProgressButton
                  onClick={handleSubmit}
                  className={classes.button}
                  classes={{
                    disabled: classes.buttonDisabled,
                    label: classes.buttonLabel
                  }}
                  color={'primary'}
                  disabled={!dirty || isSubmitting}
                  isSubmitting={isSubmitting}
                >
                  {product.id ? t('Save') : t('Add')}
                </ProgressButton>
              </Fragment>
            }
          >
            <Fragment>
              <Grid container spacing={16}>
                <GridItem xs={6}>
                  <FastField
                    name={'sku'}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <TextFieldInput
                          label={t('SKU')}
                          value={_get(values, 'sku', '')}
                          fullWidth
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem item xs={6}>
                  <FastField
                    name={'name'}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <TextFieldInput
                          label={t('Name')}
                          value={_get(values, 'name', '')}
                          fullWidth
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem item xs={12}>
                  <FastField
                    name={'description'}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <TextFieldInput
                          label={t('Description')}
                          value={_get(values, 'description', '')}
                          fullWidth
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem item xs={6}>
                  <FastField
                    name={'freightInfo.freightClassId'}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <OutlinedSelectInput
                          label={t('Class')}
                          value={_get(values, 'freightInfo.freightClassId', '')}
                          options={referenceFields.freightClasses}
                          chipped={true}
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem xs={3}>
                  <FastField
                    name={'freightInfo.nmfcItemCode'}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <TextFieldInput
                          label={t('NMFC')}
                          value={_get(values, 'freightInfo.nmfcItemCode', '')}
                          fullWidth
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem xs={3}>
                  <FastField
                    name={'freightInfo.nmfcSubCode'}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <TextFieldInput
                          label={t('Subcode')}
                          value={_get(values, 'freightInfo.nmfcSubCode', '')}
                          fullWidth
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem xs={6}>
                  <FastField
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <TextFieldInput
                          value={_get(values, 'weightInfo.weight', '')}
                          label={t('Weight')}
                          name={'weightInfo.weight'}
                          fullWidth
                          field={field}
                          form={form}
                          type="number"
                          min={0.01}
                          datatype={DATATYPES.NUMBER}
                          endAdornment={
                            UNIT_ABBREVIATIONS[
                              _get(values, 'weightInfo.weightUnit', '')
                            ]
                          }
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem xs={6}>
                  <FastField
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <TextFieldInput
                          value={_get(values, 'valueInfo.value', '')}
                          label={t('Value')}
                          name={'valueInfo.value'}
                          fullWidth
                          field={field}
                          form={form}
                          startAdornment={
                            CURRENCY_SYMBOL[
                              _get(values, 'valueInfo.currency', '')
                            ]
                          }
                          type="number"
                          min={1}
                          datatype={DATATYPES.NUMBER}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem xs={6}>
                  <FastField
                    name={'packageTypeId'}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <OutlinedSelectInput
                          label={t('Package Type')}
                          value={_get(values, 'packageTypeId', '')}
                          options={referenceFields.packageTypes}
                          chipped={true}
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem xs={6}>
                  <FastField
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <TextFieldInput
                          value={_get(values, 'pieceCount', '')}
                          label={t('Piece Count')}
                          name={'pieceCount'}
                          fullWidth
                          field={field}
                          form={form}
                          type="number"
                          min={1}
                          datatype={DATATYPES.NUMBER}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem
                  xs={3}
                  className={classnames({
                    [classes.hasMatCheckboxContainer]: values.isHazMat
                  })}
                >
                  <FastField
                    name={'isHazMat'}
                    render={({ field, form }) => (
                      <CheckboxInput
                        label={t('HazMat')}
                        field={field}
                        form={form}
                        value={values.isHazMat}
                        onChange={e => {
                          handleChange(e);
                          this._displayHazMatFields();
                        }}
                        classes={{
                          root: classnames(classes.noMarginTop),
                          label: classnames(
                            classes.headline7,
                            classes.hasMatCheckboxLabel
                          )
                        }}
                      />
                    )}
                  />
                </GridItem>
                {values.isHazMat && (
                  <Fragment>
                    <GridItem xs={9}>
                      <FastField
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <TextFieldInput
                              value={_get(
                                values,
                                'hazMat.properShippingName',
                                ''
                              )}
                              label={t('Proper Shipping Name')}
                              name={'hazMat.properShippingName'}
                              fullWidth
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={4}>
                      <FastField
                        name={'hazMat.hazMatClassId'}
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <OutlinedSelectInput
                              label={t('HazMat Class')}
                              value={_get(values, 'hazMat.hazMatClassId', '')}
                              options={referenceFields.hazMatClasses}
                              chipped={true}
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={4}>
                      <FastField
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <TextFieldInput
                              value={_get(values, 'hazMat.unNumber', '')}
                              label={t('UN/NA')}
                              name={'hazMat.unNumber'}
                              fullWidth
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={4}>
                      <FastField
                        name={'hazMat.hazMatPackingGroupId'}
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <OutlinedSelectInput
                              label={t('Pkg Grp')}
                              value={_get(
                                values,
                                'hazMat.hazMatPackingGroupId',
                                ''
                              )}
                              options={referenceFields.hazMatPackingGroups}
                              chipped={true}
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={6}>
                      <FastField
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <TextFieldInput
                              value={_get(
                                values,
                                'hazMat.contactFirstName',
                                ''
                              )}
                              label={t('Contact First Name')}
                              name={'hazMat.contactFirstName'}
                              fullWidth
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={6}>
                      <FastField
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <TextFieldInput
                              value={_get(values, 'hazMat.contactLastName', '')}
                              label={t('Contact Last Name')}
                              name={'hazMat.contactLastName'}
                              fullWidth
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={6}>
                      <FastField
                        value={_get(values, 'hazMat.contactPhone', '')}
                        fullWidth
                        name={'hazMat.contactPhone'}
                        label={t('Contact Phone')}
                        onChange={handleChange}
                        component={PhoneField}
                        country={defaultCountry}
                      />
                    </GridItem>
                  </Fragment>
                )}
              </Grid>
            </Fragment>
          </DialogForm>
        )}
      />
    );
  }
}

ProductForm.contextType = AppContext;

const translatedProductForm = withNamespaces()(ProductForm);
export default withStyles(
  combineStyles(
    styles,
    LayoutStyles,
    TypographyStyles,
    ButtonStyles,
    SpacingStyles
  )
)(translatedProductForm);
